import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, ExternalLink } from "lucide-react";
import { IntegrationProvider } from "@/hooks/useIntegrations";
import { useToast } from "@/hooks/use-toast";

interface WebhookInstructionsProps {
  provider: IntegrationProvider;
  integrationId?: string;
}

const providerDocs: Record<IntegrationProvider, string> = {
  stripe: "https://stripe.com/docs/webhooks",
  paypal: "https://developer.paypal.com/docs/api-basics/notifications/webhooks/",
  mercadopago: "https://www.mercadopago.com.br/developers/pt/docs/webhooks",
  pagseguro: "https://dev.pagseguro.uol.com.br/reference/notificacoes",
  hotmart: "https://developers.hotmart.com/docs/pt-BR/webhooks",
  kiwify: "https://kiwify.com.br/docs/webhooks",
  pagarme: "https://docs.pagar.me/docs/webhooks-1",
  vindi: "https://vindi.github.io/api-docs/dist/#/webhooks",
  iugu: "https://dev.iugu.com/reference/webhooks",
  eduzz: "https://atendimento.eduzz.com/portal/pt-br/kb/articles/webhooks",
};

export function WebhookInstructions({
  provider,
  integrationId,
}: WebhookInstructionsProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const webhookUrl = `${window.location.origin}/api/webhook-${provider}${
    integrationId ? `?integration_id=${integrationId}` : ""
  }`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      toast({
        title: "URL copiada!",
        description: "A URL do webhook foi copiada para a área de transferência.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar a URL.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 pt-4">
      <div>
        <h4 className="font-semibold mb-2">URL do Webhook</h4>
        <p className="text-sm text-muted-foreground mb-3">
          Configure esta URL no painel do {provider} para receber notificações de eventos.
        </p>
        <div className="flex gap-2">
          <Input value={webhookUrl} readOnly className="font-mono text-sm" />
          <Button
            variant="outline"
            size="icon"
            onClick={copyToClipboard}
            title="Copiar URL"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-2">Eventos recomendados</h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>Pagamentos aprovados</li>
          <li>Assinaturas criadas</li>
          <li>Assinaturas canceladas</li>
          <li>Reembolsos processados</li>
          <li>Chargebacks</li>
        </ul>
      </div>

      <div>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => window.open(providerDocs[provider], "_blank")}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Ver documentação do {provider}
        </Button>
      </div>

      <div className="bg-muted p-4 rounded-lg">
        <h4 className="font-semibold mb-2 text-sm">⚠️ Importante</h4>
        <p className="text-xs text-muted-foreground">
          Após configurar o webhook, teste o envio de um evento de teste para
          garantir que a integração está funcionando corretamente.
        </p>
      </div>
    </div>
  );
}
